import { z } from 'zod'
import { BLOOD_GROUPS } from '@/features/students/types'
import { EMPLOYMENT_TYPES } from './types'

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Use at most ${max} characters.`)
    .transform((v) => (v === '' ? null : v))
    .nullable()
const optionalId = z
  .string()
  .transform((v) => (v === '' ? null : Number(v)))
  .nullable()

export const staffSchema = z.object({
  firstName: z.string().trim().min(1, 'Enter the first name.').max(80),
  lastName: z.string().trim().min(1, 'Enter the last name.').max(80),
  gender: z.enum(['male', 'female', 'other'], { error: 'Choose a gender.' }),
  dateOfBirth: z
    .string()
    .transform((v) => (v === '' ? null : v))
    .nullable(),
  bloodGroup: z
    .union([z.literal(''), z.enum(BLOOD_GROUPS)])
    .transform((v) => (v === '' ? null : v))
    .nullable(),
  email: z
    .union([z.literal(''), z.email('Enter a valid email address.')])
    .transform((v) => (v === '' ? null : v))
    .nullable(),
  phone: optionalText(30),
  address: optionalText(2000),
  qualification: optionalText(150),
  departmentId: optionalId,
  designationId: optionalId,
  primarySubjectId: optionalId,
  employmentType: z.enum(EMPLOYMENT_TYPES),
  joiningDate: z
    .string()
    .transform((v) => (v === '' ? null : v))
    .nullable(),
  basicSalary: z
    .string()
    .transform((v) => (v.trim() === '' ? null : Number(v)))
    .nullable()
    .refine((v) => v === null || (Number.isFinite(v) && v >= 0), 'Enter a positive amount.'),
  photoFileId: z.number().nullable(),
})

export type StaffFormInput = z.input<typeof staffSchema>
export type StaffFormOutput = z.output<typeof staffSchema>

export const staffAccountSchema = z.object({
  role: z.enum(['admin', 'teacher', 'accountant', 'librarian', 'receptionist']),
  email: z.email('Enter a valid email address.'),
  password: z.string().min(8, 'Use at least 8 characters.').max(128),
})
export type StaffAccountInput = z.infer<typeof staffAccountSchema>
